import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  onFreeTrial: () => void;
  onBookDemo: () => void;
}

export default function HeroSection({ onFreeTrial, onBookDemo }: HeroSectionProps) {
  return (
    <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden">
      {/* Decorative background corner */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-50/30 -z-10 rounded-bl-[100px]"></div>

      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left: Text Content */}
        <div className="space-y-8 reveal">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100/80 border border-emerald-200 text-foreground text-sm tracking-wide font-medium">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            ISO 27001 Zertifiziert
          </div>

          {/* Headline */}
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-foreground tracking-tight">
            Nie wieder SMS-Code{' '}
            <span className="text-primary italic">verpassen.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg text-muted-foreground font-light max-w-md leading-relaxed">
            Erhalten Sie 2FA-SMS-Codes direkt in Ihrem gesicherten Dashboard. Perfekt für Geschäftsführer und Manager, die nicht immer Zugriff auf ihr Handy haben.
          </p>

          {/* CTAs */}
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={onFreeTrial}
              className="bg-primary text-primary-foreground hover:bg-blue-600 px-8 py-4 rounded-full text-sm font-medium transition-all shadow-lg shadow-primary/20 flex items-center gap-2">
              
              Kostenlos testen
              <Icon name="ArrowRightIcon" size={16} variant="outline" className="text-primary-foreground" />
            </button>
            <button
              onClick={onBookDemo}
              className="px-8 py-4 rounded-full text-sm font-medium text-foreground border border-border hover:bg-card hover:border-slate-300 transition-all">
              
              Demo buchen
            </button>
          </div>

          {/* Social Proof */}
          <div className="flex items-center gap-8 pt-4">
            {/* Avatar Group */}
            <div className="flex -space-x-3">
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_10a543b20-1767163574718.png"
                alt="Business professional in suit smiling at camera"
                className="w-10 h-10 rounded-full border-2 border-white object-cover" />
              
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_12ac98e01-1763298613824.png"
                alt="Female executive with glasses in office setting"
                className="w-10 h-10 rounded-full border-2 border-white object-cover" />
              
              <AppImage
                src="https://img.rocket.new/generatedImages/rocket_gen_img_1d160f966-1763294281395.png"
                alt="Male manager in business casual attire"
                className="w-10 h-10 rounded-full border-2 border-white object-cover" />
              
            </div>
            {/* Trust Metric */}
            <div className="flex flex-col">
              <div className="flex text-amber-500 text-xs gap-0.5">
                <Icon name="StarIcon" size={14} variant="solid" className="text-amber-500" />
                <Icon name="StarIcon" size={14} variant="solid" className="text-amber-500" />
                <Icon name="StarIcon" size={14} variant="solid" className="text-amber-500" />
                <Icon name="StarIcon" size={14} variant="solid" className="text-amber-500" />
                <Icon name="StarIcon" size={14} variant="solid" className="text-amber-500" />
              </div>
              <span className="text-xs text-muted-foreground font-medium mt-1">
                Vertraut von 500+ Unternehmen
              </span>
            </div>
          </div>
        </div>

        {/* Right: Dashboard Mockup */}
        <div className="relative reveal delay-200">
          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-tr from-blue-200/30 to-transparent rounded-[3rem] transform rotate-3"></div>
          
          {/* Main Dashboard Image */}
          <div className="relative rounded-[3rem] shadow-2xl shadow-slate-900/10 overflow-hidden tilt-image">
            <AppImage
              src="https://img.rocket.new/generatedImages/rocket_gen_img_16088bad6-1770794693151.png"
              alt="Dashboard interface showing SMS authentication codes in real-time with clean modern design"
              className="w-full h-[600px] object-cover" />
            
            {/* Overlay gradient for better readability */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
          </div>

          {/* Floating ISO Badge */}
          <div className="absolute bottom-12 -left-8 bg-white/95 backdrop-blur-sm p-4 rounded-2xl shadow-xl shadow-slate-900/10 border border-white max-w-xs animate-float">
            <div className="flex items-start gap-3">
              <div className="bg-emerald-100 p-2 rounded-full text-emerald-700">
                <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-emerald-700" />
              </div>
              <div>
                <p className="font-serif text-foreground font-medium">ISO 27001 Zertifiziert</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Höchste Sicherheitsstandards für Ihre Daten.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}